import React, { useEffect } from 'react';
import './tariff.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  Button, Col, Row,
} from '../../../components/base/BootstrapComponents';
import { getTariffData } from '../reducer';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import { createDailyTariff, editDailyTariff } from '../actions';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import Loader from '../../../components/base/Loader/Loader';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import TimePicker from '../../../components/fields/TimePicker/TimePicker';
import Switches from '../../../components/fields/Switches/Switches';
import DailyTariffDTO from '../../../dto/DailyTariffDTO';
import PricingStepsEditor, {
  createEmptyPricingStep,
  normalizePricingPayload,
  normalizePricingStepsForForm,
} from './PricingStepsEditor';

export default function DailyTariffForm(props) {
  const {
    handleClose = () => {}, handleEdit = () => {}, loadData = () => {}, hasView = false,
  } = props;
  const dispatch = useDispatch();
  const {
    currentDailyTariff,
  } = useSelector(getTariffData);

  const defaultValues = currentDailyTariff?.id
    ? {
      ...currentDailyTariff,
      type: 'single',
      pricing_mode: currentDailyTariff?.pricing_mode || 'flat',
      pricing_steps: normalizePricingStepsForForm(currentDailyTariff?.pricing_steps),
    }
    : {
      is_active: true,
      type: 'single',
      pricing_mode: 'flat',
      pricing_steps: [],
    };
  const {
    control, getValues, watch, setValue,
  } = useForm({
    defaultValues,
  });
  const pricingMode = watch('pricing_mode') || 'flat';
  const pricingSteps = watch('pricing_steps') || [];
  const timeUnit = watch('time_unit') || currentDailyTariff?.time_unit || 'hour';

  useEffect(() => {
    if (pricingMode === 'tiered' && !pricingSteps.length) {
      setValue('pricing_steps', [createEmptyPricingStep()]);
    }
    if (pricingMode !== 'tiered' && pricingSteps.length) {
      setValue('pricing_steps', []);
    }
  }, [pricingMode, pricingSteps.length, setValue]);

  const handleSave = () => {
    const isUpdate = currentDailyTariff?.id;
    const action = isUpdate ? editDailyTariff : createDailyTariff;
    const data = getValues();
    const normalizedSteps = normalizePricingPayload(data.pricing_mode, data.pricing_steps);
    const payload = {
      ...data,
      type: 'single',
      pricing_mode: data.pricing_mode || 'flat',
    };

    if (payload.pricing_mode === 'tiered') {
      payload.pricing_steps = normalizedSteps;
      payload.cost = normalizedSteps?.[0]?.cost ?? 0;
    } else {
      delete payload.pricing_steps;
    }

    dispatch(action(payload))
      .then(() => {
        loadData();
        handleClose();
        notificationDecorator({
          title: isUpdate ? 'Редактирование дневного тарифа' : 'Создание дневного тарифа',
          message: isUpdate
            ? 'Редактирование дневного тарифа прошло успешно'
            : 'Создание дневного тарифа прошло успешно',
        });
      })
      .catch(responseLogWithNotification);
  };

  const dailyTariffDTO = new DailyTariffDTO();
  const attributes = dailyTariffDTO.getAttributesByName();

  const vehicleTypeOptions = [
    { nameId: 'vehicle.type.car', value: 'car' },
    { nameId: 'vehicle.type.bus', value: 'bus' },
    { nameId: 'vehicle.type.motorcycle', value: 'motorcycle' },
    { nameId: 'vehicle.type.truck', value: 'truck' },
    { nameId: 'vehicle.type.all', value: 'all' },
  ];

  const timeUnitOptions = [
    { nameId: 'time.unit.minute', value: 'minute' },
    { nameId: 'time.unit.hour', value: 'hour' },
    { nameId: 'time.unit.day', value: 'day' },
  ];

  const pricingModeOptions = [
    { nameId: 'pricing.mode.flat', value: 'flat' },
    { nameId: 'pricing.mode.tiered', value: 'tiered' },
  ];

  return (
    <div className="page-container tariff-form">
      <Loader loading={false} />
      <div className="inputs-box">
        <PageHeader header={currentDailyTariff?.id ? 'Редактирование дневного тарифа' : 'Создание дневного тарифа'} />
      </div>
      <div className="plp-table g-0">
        <div className="frame py-3 d-flex flex-column px-3">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <div className="h4">
              Данные дневного тарифа
            </div>
          </div>
          <Row className="h-100">
            <Col columns={6}>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.date}
                addPlaceholder
                component={DatePicker}
                disabled={hasView}
                withLabel
                changeFormat="YYYY-MM-DD"
              />
              <Row>
                <Col columns={6}>
                  <FieldDTO
                    control={control}
                    type="text"
                    className="mb-2"
                    dtoObject={attributes.start_time}
                    addPlaceholder
                    component={TimePicker}
                    disabled={hasView}
                  />
                </Col>
                <Col columns={6}>
                  <FieldDTO
                    control={control}
                    type="text"
                    className="mb-2"
                    dtoObject={attributes.end_time}
                    addPlaceholder
                    component={TimePicker}
                    disabled={hasView}
                  />
                </Col>
              </Row>
              <Row>
                <Col columns={6}>
                  <FieldDTO
                    control={control}
                    type="text"
                    className="mb-2"
                    dtoObject={attributes.vehicle_type}
                    addPlaceholder
                    component={CustomSelectInput}
                    disabled={hasView}
                    options={vehicleTypeOptions}
                    withLabel
                  />
                </Col>
                <Col columns={6}>
                  <FieldDTO
                    control={control}
                    type="text"
                    className="mb-2"
                    dtoObject={attributes.time_unit}
                    addPlaceholder
                    component={CustomSelectInput}
                    disabled={hasView}
                    options={timeUnitOptions}
                    withLabel
                  />
                </Col>
              </Row>
              <Row>
                <Col columns={6}>
                  <FieldDTO
                    control={control}
                    type="number"
                    className="mb-2"
                    dtoObject={attributes.free_duration}
                    addPlaceholder
                    component={TextInputWithLabel}
                    disabled={hasView}
                  />
                </Col>
                {pricingMode !== 'tiered'
                  ? (
                    <Col columns={6}>
                      <FieldDTO
                        control={control}
                        type="number"
                        className="mb-2"
                        dtoObject={attributes.cost}
                        addPlaceholder
                        component={TextInputWithLabel}
                        disabled={hasView}
                      />
                    </Col>
                  )
                  : null}
              </Row>
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={{
                  name: 'pricing_mode',
                  type: String,
                  validators: {},
                  labelKey: 'pricing.mode',
                }}
                addPlaceholder
                component={CustomSelectInput}
                options={pricingModeOptions}
                disabled={hasView}
                withLabel
              />
              {pricingMode === 'tiered'
                ? (
                  <PricingStepsEditor
                    control={control}
                    pricingSteps={pricingSteps}
                    setPricingSteps={steps => setValue('pricing_steps', steps)}
                    disabled={hasView}
                    timeUnit={timeUnit}
                  />
                )
                : null}
              <FieldDTO
                control={control}
                type="text"
                className="mb-2"
                dtoObject={attributes.is_active}
                addPlaceholder
                component={Switches}
                disabled={hasView}
              />
              <div className="text-center justify-content-center p-3">
                {!hasView
                  ? <Button className="me-3 px-3" onClick={handleSave}>Сохранить</Button>
                  : <Button className="me-3 px-3" onClick={handleEdit(currentDailyTariff)}>Редактировать</Button>}

                <Button variant="secondary" className="px-3" onClick={handleClose}>Закрыть</Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
